import { useState, useEffect, useCallback } from "react";
import { Bell, Gift, Cake, UserPlus, Heart, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, dateLocale, notifText } from "@/lib/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const typeIcon = { reservation_confirm: Gift, birthday_reminder: Cake, follow: UserPlus, thank_you: Heart, gift_idea: Lightbulb };

export const NotificationBell = () => {
  const { t, lang } = useLang();
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setData(res.data);
    } catch (err) {
      console.error("notifications load failed", err);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o && data.unread_count > 0) {
      try {
        await api.post("/notifications/mark-read");
        setData((d) => ({ ...d, unread_count: 0 }));
      } catch (err) {
        console.error("mark-read failed", err);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button aria-label={t("notifications")} data-testid="notification-bell"
          className="relative w-9 h-9 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/70 transition-colors">
          <Bell size={17} />
          {data.unread_count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
              {data.unread_count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-2xl p-2" data-testid="notification-panel">
        <p className="font-heading font-semibold text-sm px-3 py-2">{t("notifications")}</p>
        {data.notifications.length === 0 ? (
          <p className="text-sm text-muted-foreground px-3 py-6 text-center">{t("no_notifications")}</p>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-1">
            {data.notifications.map((n) => {
              const Icon = typeIcon[n.type] || Bell;
              return (
                <div key={n.notification_id} className={`flex gap-3 px-3 py-2.5 rounded-xl ${!n.read ? "bg-primary/5" : ""}`}>
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    <Icon size={14} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm leading-snug">{notifText(t, n)}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleDateString(dateLocale(lang))}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
